/**
 * Interview Preparation Data
 * Career-specific interview questions, sample answers, and tips
 */

import { CareerPath } from './careerPaths';

export interface InterviewQuestion {
  question: string;
  category: 'behavioral' | 'technical' | 'situational' | 'culture';
  difficulty: 'entry' | 'mid' | 'senior';
  sampleAnswer: string;
  tips: string[];
  redFlags: string[];
}

export interface InterviewPrepData {
  commonQuestions: InterviewQuestion[];
  starExamples: {
    situation: string;
    task: string;
    action: string;
    result: string;
  }[];
  questionsToAsk: {
    question: string;
    why: string;
    category: 'role' | 'team' | 'growth' | 'culture';
  }[];
  dresscode: string;
  interviewFormats: string[];
  commonMistakes: string[];
  dayBeforeTips: string[];
}

// Career-specific interview prep data
const INTERVIEW_PREP_DATA: Record<string, InterviewPrepData> = {
  'software-developer': {
    commonQuestions: [
      {
        question: "Tell me about a challenging bug you fixed and how you approached it.",
        category: 'technical',
        difficulty: 'entry',
        sampleAnswer: "In my last project, we had a memory leak causing crashes after 2 hours of use. I used profiling tools to identify the source - unclosed database connections in a loop. I implemented connection pooling and added proper cleanup in finally blocks, reducing memory usage by 60% and eliminating crashes.",
        tips: [
          "Use the STAR method to structure your answer",
          "Be specific about the debugging tools you used",
          "Quantify the impact of your fix when possible"
        ],
        redFlags: [
          "Blaming others for the bug",
          "Not explaining your debugging process",
          "Unable to describe technical details"
        ]
      },
      {
        question: "How do you stay current with new technologies?",
        category: 'behavioral',
        difficulty: 'entry',
        sampleAnswer: "I dedicate 5 hours weekly to learning. I follow tech blogs like Dev.to and Hacker News, take Udemy courses, and build side projects to practice new skills. Recently I learned Docker by containerizing my personal projects, which I now use in my daily work.",
        tips: [
          "Show genuine curiosity and self-motivation",
          "Mention specific resources you use",
          "Give examples of skills you've recently learned"
        ],
        redFlags: [
          "Saying you don't have time to learn",
          "Only learning what's required for work",
          "No concrete examples of recent learning"
        ]
      },
      {
        question: "Describe a time you disagreed with a technical decision. How did you handle it?",
        category: 'behavioral',
        difficulty: 'mid',
        sampleAnswer: "Our team lead wanted to use a NoSQL database for structured financial data. I respectfully presented benchmarks showing SQL would be faster for our query patterns. We had a constructive discussion, and he agreed to run a POC. The SQL approach proved 40% faster, and he appreciated my data-driven approach.",
        tips: [
          "Show you can disagree professionally",
          "Emphasize data-driven decision making",
          "Highlight collaboration over conflict"
        ],
        redFlags: [
          "Showing inability to compromise",
          "Speaking negatively about colleagues",
          "Not backing up opinions with reasoning"
        ]
      },
      {
        question: "Walk me through how you would design a URL shortener.",
        category: 'technical',
        difficulty: 'mid',
        sampleAnswer: "I'd start by clarifying requirements: expected scale, analytics needs, expiration policies. For the core system, I'd use a hash function to generate short codes, store mappings in a distributed cache like Redis for speed with database backup. I'd handle collisions with retry logic and implement rate limiting for abuse prevention.",
        tips: [
          "Always clarify requirements first",
          "Think out loud about trade-offs",
          "Consider scale, reliability, and edge cases"
        ],
        redFlags: [
          "Jumping straight to code without planning",
          "Not considering scalability",
          "Ignoring security concerns"
        ]
      }
    ],
    starExamples: [
      {
        situation: "Our e-commerce site was experiencing 10-second page loads during peak traffic, causing cart abandonment.",
        task: "I was tasked with improving performance to under 2 seconds while maintaining all functionality.",
        action: "I profiled the application, identified N+1 query issues, implemented Redis caching, and added lazy loading for images. I also set up performance monitoring.",
        result: "Page load time dropped to 1.2 seconds, cart abandonment decreased 35%, and we could handle 3x more concurrent users."
      },
      {
        situation: "A junior developer was struggling with a complex feature and falling behind on sprint commitments.",
        task: "As a team member, I needed to help without taking over their work or undermining their growth.",
        action: "I offered to pair program for an hour daily, breaking the problem into smaller tasks. I asked guiding questions rather than giving direct solutions.",
        result: "They completed the feature on time, gained confidence, and later mentored another new hire using similar techniques."
      }
    ],
    questionsToAsk: [
      {
        question: "What does the typical development workflow look like here?",
        why: "Understand their processes, tools, and how code gets from idea to production",
        category: 'role'
      },
      {
        question: "How do you handle technical debt?",
        why: "Shows you care about code quality and long-term maintainability",
        category: 'team'
      },
      {
        question: "What opportunities are there for learning and professional development?",
        why: "Demonstrates growth mindset and long-term thinking",
        category: 'growth'
      },
      {
        question: "Can you describe the team's culture around code reviews?",
        why: "Reveals collaboration style and openness to feedback",
        category: 'culture'
      }
    ],
    dresscode: "Business casual (button-down or nice polo, slacks). For startups, smart casual is usually fine. When in doubt, ask the recruiter.",
    interviewFormats: [
      "Technical phone screen (30-60 min)",
      "Take-home coding assignment (2-4 hours)",
      "Live coding interview (45-60 min)",
      "System design discussion (45-60 min)",
      "Behavioral/culture fit interview (30-45 min)"
    ],
    commonMistakes: [
      "Not asking clarifying questions before coding",
      "Staying silent while problem-solving",
      "Giving up too quickly on hard problems",
      "Not testing code or considering edge cases",
      "Badmouthing previous employers or colleagues"
    ],
    dayBeforeTips: [
      "Review the company's tech blog and recent product updates",
      "Practice explaining your past projects out loud",
      "Prepare your development environment for any live coding",
      "Get 8 hours of sleep - cognitive performance matters",
      "Have backup plans for technical issues (phone hotspot, etc.)"
    ]
  },

  'cybersecurity-analyst': {
    commonQuestions: [
      {
        question: "Walk me through how you would investigate a potential security breach.",
        category: 'technical',
        difficulty: 'entry',
        sampleAnswer: "First, I'd contain the threat to prevent spread - isolating affected systems. Then I'd preserve evidence by taking forensic images. I'd analyze logs across SIEM, firewalls, and endpoints to establish a timeline. I'd identify the attack vector, assess impact, and document everything for the incident report and lessons learned.",
        tips: [
          "Show methodical thinking",
          "Mention specific tools you'd use",
          "Emphasize documentation and communication"
        ],
        redFlags: [
          "Jumping to conclusions without evidence",
          "Forgetting to preserve forensic evidence",
          "Not mentioning stakeholder communication"
        ]
      },
      {
        question: "How do you stay updated on the latest threats and vulnerabilities?",
        category: 'behavioral',
        difficulty: 'entry',
        sampleAnswer: "I subscribe to threat intelligence feeds like US-CERT and Krebs on Security. I'm active in security communities on Twitter and Discord. I hold a Security+ certification and am working toward CISSP. I also run a home lab where I practice new attack techniques to understand how to defend against them.",
        tips: [
          "Show genuine passion for security",
          "Mention specific sources and communities",
          "Highlight hands-on practice"
        ],
        redFlags: [
          "Only relying on formal training",
          "Not mentioning any threat intel sources",
          "No evidence of continuous learning"
        ]
      },
      {
        question: "Describe a time you had to explain a security risk to non-technical stakeholders.",
        category: 'situational',
        difficulty: 'mid',
        sampleAnswer: "I needed to convince leadership to approve emergency patching that would cause 2 hours of downtime. I avoided jargon and used an analogy: 'This is like having a broken lock on our front door. The fix takes 2 hours, but leaving it means anyone can walk in.' I quantified potential breach costs. They approved immediately.",
        tips: [
          "Use analogies and plain language",
          "Quantify risks in business terms",
          "Show empathy for their priorities"
        ],
        redFlags: [
          "Using too much technical jargon",
          "Talking down to stakeholders",
          "Not connecting to business impact"
        ]
      }
    ],
    starExamples: [
      {
        situation: "We detected unusual outbound traffic from our network during routine monitoring.",
        task: "I needed to determine if this was a threat, contain it if so, and report findings to leadership.",
        action: "I isolated the suspicious machine, analyzed traffic patterns, and discovered malware beaconing to a C2 server. I traced the infection to a phishing email, blocked the domain, and scanned all endpoints for the malware signature.",
        result: "We contained the breach within 2 hours with no data exfiltration. I created a phishing awareness training program that reduced click rates by 60%."
      }
    ],
    questionsToAsk: [
      {
        question: "What security frameworks do you follow (NIST, ISO 27001, etc.)?",
        why: "Shows you understand compliance and want to align with established practices",
        category: 'role'
      },
      {
        question: "How does the security team interact with development and IT operations?",
        why: "Reveals if security is embedded in culture or siloed",
        category: 'team'
      },
      {
        question: "What's the biggest security challenge you're currently facing?",
        why: "Shows genuine interest and gives insight into the role",
        category: 'role'
      }
    ],
    dresscode: "Business casual for most roles. Government/defense contractors may expect more formal attire.",
    interviewFormats: [
      "Technical screening (security concepts, tools)",
      "Scenario-based problem solving",
      "CTF or hands-on lab challenge",
      "Behavioral/culture interview",
      "Background check discussion"
    ],
    commonMistakes: [
      "Focusing only on offensive security (red team) without defensive knowledge",
      "Not understanding compliance requirements",
      "Being unable to communicate risks to non-technical audiences",
      "Overconfidence about 'unhackable' solutions"
    ],
    dayBeforeTips: [
      "Review recent CVEs and security news",
      "Brush up on incident response frameworks",
      "Prepare examples of security improvements you've made",
      "Know the company's industry-specific compliance requirements"
    ]
  },

  'project-manager': {
    commonQuestions: [
      {
        question: "Tell me about a project that didn't go as planned. What did you do?",
        category: 'behavioral',
        difficulty: 'entry',
        sampleAnswer: "We were launching a product feature when a key engineer left mid-project. I immediately reassessed our timeline, identified the critical path items, and worked with HR to bring in a contractor. I communicated the delay to stakeholders with a revised plan. We delivered 3 weeks late but with full scope, and the client appreciated our transparency.",
        tips: [
          "Show accountability, not blame",
          "Highlight your problem-solving process",
          "Emphasize communication and transparency"
        ],
        redFlags: [
          "Blaming team members or circumstances",
          "Not showing what you learned",
          "Hiding problems from stakeholders"
        ]
      },
      {
        question: "How do you handle scope creep?",
        category: 'situational',
        difficulty: 'mid',
        sampleAnswer: "I use a formal change request process. When a stakeholder requests a new feature, I document the ask, estimate impact on timeline and budget, and present trade-offs. I ask: 'We can add this if we push the deadline by 2 weeks or drop feature X. Which do you prefer?' This keeps scope changes conscious and documented.",
        tips: [
          "Show you have a systematic process",
          "Emphasize trade-off discussions",
          "Mention documentation and approval flows"
        ],
        redFlags: [
          "Just saying 'no' to all changes",
          "Accepting all changes without impact analysis",
          "No formal change management process"
        ]
      },
      {
        question: "Describe your communication style with different stakeholders.",
        category: 'behavioral',
        difficulty: 'entry',
        sampleAnswer: "I tailor communication to the audience. With executives, I lead with outcomes and business impact in 5-minute updates. With technical teams, I dive into details and blockers in daily standups. With clients, I focus on value delivered and upcoming milestones. I use the right medium too - Slack for quick updates, email for documentation, meetings for complex discussions.",
        tips: [
          "Show adaptability",
          "Give specific examples for different audiences",
          "Mention different communication channels"
        ],
        redFlags: [
          "One-size-fits-all communication",
          "Over-communicating or under-communicating",
          "Not adjusting to audience needs"
        ]
      }
    ],
    starExamples: [
      {
        situation: "Two key stakeholders had conflicting priorities for our project - one wanted speed, the other wanted more features.",
        task: "I needed to align both stakeholders and keep the project moving forward without damaging relationships.",
        action: "I scheduled a joint meeting, helped each understand the other's perspective, and proposed an MVP approach: ship core features fast, then iterate. I created a visual roadmap showing how both priorities would be met over time.",
        result: "Both stakeholders agreed to the approach. We launched the MVP 2 weeks early, and the iterative releases kept both parties satisfied."
      }
    ],
    questionsToAsk: [
      {
        question: "What project management tools and methodologies does the team use?",
        why: "Shows you can adapt to their processes",
        category: 'role'
      },
      {
        question: "How are project success metrics defined and measured?",
        why: "Demonstrates focus on outcomes, not just outputs",
        category: 'role'
      },
      {
        question: "What's the typical team size and composition for projects here?",
        why: "Helps you understand scope and resource management",
        category: 'team'
      }
    ],
    dresscode: "Business casual to business professional, depending on the industry. Tech companies lean casual; consulting/finance lean formal.",
    interviewFormats: [
      "Behavioral interview (STAR method heavy)",
      "Case study or scenario presentation",
      "Stakeholder simulation exercise",
      "PM tool proficiency discussion",
      "Culture fit interview"
    ],
    commonMistakes: [
      "Focusing too much on tools instead of leadership",
      "Not having concrete examples with metrics",
      "Unable to discuss failures and lessons learned",
      "Not showing stakeholder management skills"
    ],
    dayBeforeTips: [
      "Prepare 5-7 STAR stories covering different PM scenarios",
      "Research the company's project types and methodologies",
      "Know the difference between Agile, Scrum, Waterfall, and when to use each",
      "Prepare questions that show strategic thinking"
    ]
  },

  'data-analyst': {
    commonQuestions: [
      {
        question: "Walk me through your process for analyzing a new dataset.",
        category: 'technical',
        difficulty: 'entry',
        sampleAnswer: "First, I understand the business question we're trying to answer. Then I explore the data: check for nulls, outliers, distributions, and data types. I clean and transform as needed. I use visualizations to find patterns and test hypotheses. Finally, I translate findings into actionable recommendations, always considering the audience's technical level.",
        tips: [
          "Start with the business question",
          "Show systematic approach",
          "Emphasize communication of results"
        ],
        redFlags: [
          "Jumping straight to analysis without understanding context",
          "Not mentioning data quality checks",
          "Forgetting to communicate findings clearly"
        ]
      },
      {
        question: "Tell me about a time your analysis led to a business decision.",
        category: 'behavioral',
        difficulty: 'mid',
        sampleAnswer: "I analyzed customer churn data and found that users who didn't engage in the first 7 days had 80% churn rate. I presented this to the product team with a recommendation for an onboarding email sequence. They implemented it, and 90-day retention improved by 25%, worth $2M annually.",
        tips: [
          "Connect analysis to business outcomes",
          "Quantify the impact when possible",
          "Show the full journey from data to decision"
        ],
        redFlags: [
          "Analysis without business impact",
          "Not being able to quantify results",
          "Taking sole credit for team efforts"
        ]
      },
      {
        question: "How do you handle conflicting data or unexpected results?",
        category: 'situational',
        difficulty: 'mid',
        sampleAnswer: "I first verify the data source and quality. Then I look for confounding variables or sampling issues. If the data is valid but unexpected, I investigate why - sometimes the 'wrong' answer reveals something important. I document my validation process and present findings with appropriate caveats about data limitations.",
        tips: [
          "Show intellectual honesty",
          "Demonstrate validation skills",
          "Emphasize transparent communication"
        ],
        redFlags: [
          "Cherry-picking data to fit expectations",
          "Not investigating anomalies",
          "Hiding uncertainty from stakeholders"
        ]
      }
    ],
    starExamples: [
      {
        situation: "Marketing claimed their campaign drove a 40% sales increase, but the data seemed off.",
        task: "I needed to validate the claim and provide accurate attribution analysis.",
        action: "I built a control group analysis, accounting for seasonality and other campaigns. I found the true lift was 12% - still good, but not 40%. I presented the methodology clearly, showing how the original analysis was confounded by holiday seasonality.",
        result: "Leadership appreciated the accurate picture. We adjusted future campaign budgets based on real ROI, saving $200K that was being misallocated."
      }
    ],
    questionsToAsk: [
      {
        question: "What tools and technologies does the data team use?",
        why: "Ensures alignment with your skills and shows adaptability",
        category: 'role'
      },
      {
        question: "How does the data team collaborate with other departments?",
        why: "Shows you understand the cross-functional nature of data work",
        category: 'team'
      },
      {
        question: "What's the biggest data challenge the team is currently facing?",
        why: "Demonstrates genuine interest and problem-solving mindset",
        category: 'role'
      }
    ],
    dresscode: "Business casual for most companies. Tech startups may be more casual.",
    interviewFormats: [
      "SQL/Python technical assessment",
      "Case study analysis (given dataset, find insights)",
      "Take-home data project",
      "Presentation of past analysis work",
      "Behavioral interview"
    ],
    commonMistakes: [
      "Focusing on technical skills without business context",
      "Not being able to explain analysis to non-technical audiences",
      "Ignoring data quality issues",
      "Not asking clarifying questions about the business problem"
    ],
    dayBeforeTips: [
      "Practice SQL queries, especially window functions and joins",
      "Review common statistical concepts (p-values, confidence intervals)",
      "Prepare to explain past projects in business terms",
      "Have Excel/Python/R environment ready for any live exercises"
    ]
  },

  'ux-designer': {
    commonQuestions: [
      {
        question: "Walk me through your design process from research to final design.",
        category: 'technical',
        difficulty: 'entry',
        sampleAnswer: "I start with understanding users through interviews, surveys, or existing research. I define the problem and create personas. Then I sketch low-fidelity wireframes and get early feedback. I iterate to high-fidelity prototypes in Figma, test with real users, and refine based on findings. I collaborate closely with developers during implementation and monitor metrics post-launch.",
        tips: [
          "Show a systematic, user-centered approach",
          "Mention specific tools you use",
          "Emphasize iteration and testing"
        ],
        redFlags: [
          "Skipping research and jumping to design",
          "Not mentioning user testing",
          "Design in isolation without stakeholder input"
        ]
      },
      {
        question: "Tell me about a time you had to advocate for the user against stakeholder pressure.",
        category: 'behavioral',
        difficulty: 'mid',
        sampleAnswer: "A PM wanted to add 5 new features to our checkout flow. I ran usability tests showing that more steps increased cart abandonment by 15% per step. I proposed an alternative: a progressive disclosure approach that kept features accessible but simplified the default flow. The PM agreed after seeing the data, and conversion improved 22%.",
        tips: [
          "Back up advocacy with data",
          "Show collaboration, not confrontation",
          "Propose solutions, not just objections"
        ],
        redFlags: [
          "Being inflexible or combative",
          "Not having data to support positions",
          "Giving up too easily on user needs"
        ]
      },
      {
        question: "How do you measure the success of your designs?",
        category: 'technical',
        difficulty: 'mid',
        sampleAnswer: "I define success metrics upfront based on the goal - could be task completion rate, time on task, error rate, or NPS. I use analytics tools to track these post-launch. I also conduct qualitative follow-ups to understand the 'why' behind the numbers. For example, a recent redesign improved task completion from 65% to 89% and reduced support tickets by 40%.",
        tips: [
          "Connect design to measurable outcomes",
          "Mention both quantitative and qualitative methods",
          "Give specific examples with numbers"
        ],
        redFlags: [
          "Only caring about aesthetics",
          "Not knowing how to measure success",
          "No post-launch evaluation"
        ]
      }
    ],
    starExamples: [
      {
        situation: "Our mobile app had a 40% drop-off rate during the signup flow.",
        task: "I was asked to redesign the signup experience to improve completion rates.",
        action: "I conducted user interviews to identify pain points, found the email verification step was confusing. I redesigned with clearer instructions, added inline validation, and reduced form fields from 8 to 4. I A/B tested the new flow.",
        result: "Signup completion increased from 60% to 85%, adding 10,000 new users per month."
      }
    ],
    questionsToAsk: [
      {
        question: "How does the design team conduct user research?",
        why: "Shows you value research-driven design",
        category: 'team'
      },
      {
        question: "What's the design team's relationship with product and engineering?",
        why: "Reveals collaboration culture and design influence",
        category: 'team'
      },
      {
        question: "How do you handle design debt and iteration on existing products?",
        why: "Shows realistic understanding of design work beyond greenfield projects",
        category: 'role'
      }
    ],
    dresscode: "Smart casual to business casual. Creative industries allow more expression, but err on the side of polished.",
    interviewFormats: [
      "Portfolio presentation and deep-dive",
      "Design challenge (whiteboard or take-home)",
      "Critique session on existing designs",
      "Collaborative design exercise",
      "Behavioral/culture fit interview"
    ],
    commonMistakes: [
      "Presenting only final designs without process",
      "Not explaining your design decisions",
      "Ignoring accessibility considerations",
      "Not having examples of iteration based on feedback"
    ],
    dayBeforeTips: [
      "Prepare to walk through 2-3 portfolio pieces in depth",
      "Practice articulating design decisions and trade-offs",
      "Research the company's product and identify potential UX improvements",
      "Have Figma or your design tool ready for any live exercises"
    ]
  }
};

// Generic interview prep for careers without specific data
const GENERIC_INTERVIEW_PREP: InterviewPrepData = {
  commonQuestions: [
    {
      question: "Tell me about yourself.",
      category: 'behavioral',
      difficulty: 'entry',
      sampleAnswer: "I'm a [your role] with [X years] of experience in [industry]. I've developed strong skills in [key skills] through [notable experiences]. I'm passionate about [relevant interest] and excited about this opportunity because [connection to role].",
      tips: [
        "Keep it to 2 minutes max",
        "Focus on professional, not personal",
        "End with why you're interested in this role"
      ],
      redFlags: [
        "Rambling or going too long",
        "Reciting your entire resume",
        "Not connecting to the role"
      ]
    },
    {
      question: "What's your greatest strength?",
      category: 'behavioral',
      difficulty: 'entry',
      sampleAnswer: "My greatest strength is [specific skill]. For example, [concrete example with outcome]. This has helped me [business impact], and I'm excited to bring this skill to [company name].",
      tips: [
        "Choose a strength relevant to the role",
        "Provide a specific example",
        "Quantify impact when possible"
      ],
      redFlags: [
        "Vague or generic answers",
        "Humble bragging",
        "Choosing a strength irrelevant to the role"
      ]
    },
    {
      question: "Where do you see yourself in 5 years?",
      category: 'behavioral',
      difficulty: 'entry',
      sampleAnswer: "In 5 years, I see myself having grown into a [senior role] where I'm [key responsibilities]. I want to deepen my expertise in [relevant area] and contribute to [company's mission]. This role is an ideal step because [connection].",
      tips: [
        "Show ambition but be realistic",
        "Align with company's growth trajectory",
        "Focus on skills and impact, not titles"
      ],
      redFlags: [
        "Saying 'I want your job'",
        "Goals unrelated to the company",
        "No clear direction"
      ]
    },
    {
      question: "Why are you leaving your current job?",
      category: 'behavioral',
      difficulty: 'entry',
      sampleAnswer: "I've learned a lot at my current company and contributed [specific achievement]. Now I'm looking for [what you want] - more [growth/challenge/impact/leadership]. This role offers [specific opportunity] that aligns with my goals.",
      tips: [
        "Stay positive about current employer",
        "Focus on what you're moving toward, not away from",
        "Be honest but diplomatic"
      ],
      redFlags: [
        "Badmouthing current employer",
        "Only talking about money",
        "Vague or evasive answers"
      ]
    },
    {
      question: "Tell me about a time you failed.",
      category: 'behavioral',
      difficulty: 'mid',
      sampleAnswer: "Early in my career, I [specific failure]. It happened because [honest reason]. I learned [specific lesson] and since then I've [behavior change]. For example, [evidence of improvement].",
      tips: [
        "Choose a real failure, not a humble brag",
        "Show what you learned",
        "Demonstrate how you've changed"
      ],
      redFlags: [
        "Claiming you've never failed",
        "Blaming others",
        "Not showing what you learned"
      ]
    }
  ],
  starExamples: [
    {
      situation: "Our team was facing a tight deadline with a critical project deliverable at risk.",
      task: "I needed to find a way to accelerate our timeline without sacrificing quality.",
      action: "I identified bottlenecks, proposed a parallel work approach, and coordinated with stakeholders to prioritize the most critical features for the initial release.",
      result: "We delivered on time with core functionality. The phased approach was so successful that we adopted it as a standard practice for future projects."
    }
  ],
  questionsToAsk: [
    {
      question: "What does success look like in this role in the first 90 days?",
      why: "Shows you're thinking about impact and sets clear expectations",
      category: 'role'
    },
    {
      question: "What are the biggest challenges the team is facing right now?",
      why: "Demonstrates problem-solving mindset and genuine interest",
      category: 'team'
    },
    {
      question: "How would you describe the company culture?",
      why: "Helps you assess fit and shows you care about environment",
      category: 'culture'
    },
    {
      question: "What opportunities are there for professional development?",
      why: "Shows growth mindset and long-term thinking",
      category: 'growth'
    }
  ],
  dresscode: "Business casual is typically safe. When in doubt, ask the recruiter or dress one level up from the company's typical attire.",
  interviewFormats: [
    "Phone/video screening (20-30 min)",
    "Hiring manager interview (45-60 min)",
    "Skills assessment or work sample",
    "Team interviews/panel discussion",
    "Final round with senior leadership"
  ],
  commonMistakes: [
    "Not researching the company beforehand",
    "Arriving late or having technical issues",
    "Not preparing questions to ask",
    "Speaking negatively about past employers",
    "Not following up after the interview"
  ],
  dayBeforeTips: [
    "Research the company, recent news, and interviewers on LinkedIn",
    "Prepare 5-7 STAR stories for behavioral questions",
    "Plan your outfit and test any technology",
    "Get a good night's sleep - you'll perform better rested",
    "Prepare thoughtful questions to ask"
  ]
};

/**
 * Get interview prep data for a career
 */
export function getInterviewPrep(career: CareerPath): InterviewPrepData {
  // Try to find career-specific data
  const careerId = career.id.toLowerCase();

  // Check for exact match
  if (INTERVIEW_PREP_DATA[careerId]) {
    return INTERVIEW_PREP_DATA[careerId];
  }

  // Check for partial matches
  for (const key of Object.keys(INTERVIEW_PREP_DATA)) {
    if (careerId.includes(key) || key.includes(careerId)) {
      return INTERVIEW_PREP_DATA[key];
    }
  }

  // Check by keywords
  const careerKeywords = career.keywords?.map(k => k.toLowerCase()) || [];
  for (const key of Object.keys(INTERVIEW_PREP_DATA)) {
    if (careerKeywords.some(kw => key.includes(kw) || kw.includes(key))) {
      return INTERVIEW_PREP_DATA[key];
    }
  }

  // Return generic data with career-specific customization
  return {
    ...GENERIC_INTERVIEW_PREP,
    questionsToAsk: GENERIC_INTERVIEW_PREP.questionsToAsk.map(q => ({
      ...q,
      question: q.question.replace('[role]', career.title)
    }))
  };
}

/**
 * Get category label for display
 */
export function getCategoryLabel(category: InterviewQuestion['category']): string {
  const labels = {
    behavioral: 'Behavioral',
    technical: 'Technical',
    situational: 'Situational',
    culture: 'Culture Fit'
  };
  return labels[category];
}

/**
 * Get difficulty label and color
 */
export function getDifficultyInfo(difficulty: InterviewQuestion['difficulty']): { label: string; color: string } {
  const info = {
    entry: { label: 'Entry Level', color: '#10b981' },
    mid: { label: 'Mid Level', color: '#f59e0b' },
    senior: { label: 'Senior Level', color: '#8b5cf6' }
  };
  return info[difficulty];
}
